import React from 'react';
import { createPortal } from 'react-dom';
import { Activity } from 'lucide-react';

import { POP_CLASS, firstOf, useHoverPop } from '../workspace/DeskKit';
import { activityLine, ago } from './clientActivityText';

// New from the client, on their desk row (Nic 2026-10-23): a small mark by
// their name, not the whole update in the row. Hover (or tap) shows what's
// new; opening the client clears it. Looks and behaves like the to-do mark.
const ActivityMark = ({ name, activity, labels = {}, now = new Date() }) => {
  const { open, setOpen, show, hide, timer, btnRef, popRef, style } = useHoverPop(280);
  if (!activity?.count) return null;
  const items = activity.items?.length ? activity.items : [activity.latest].filter(Boolean);
  const more = activity.count - items.length;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`${activity.count} new from ${name || 'this client'}`}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          clearTimeout(timer.current);
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="inline-flex h-[22px] shrink-0 items-center gap-[4px] self-center rounded-md bg-sky-100 px-[6px] text-[11.5px] font-bold tabular-nums text-sky-800 outline-none ring-1 ring-sky-200/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-sky-400/15 dark:text-sky-200 dark:ring-sky-400/20"
      >
        <Activity size={12} strokeWidth={2.5} />
        {activity.count}
      </button>
      {open && style
        ? createPortal(
            <div
              ref={popRef}
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={hide}
              onClick={(e) => e.stopPropagation()}
              style={{ ...style, overflowY: 'visible', maxHeight: 'none' }}
              className={POP_CLASS}
            >
              <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-sky-700 dark:text-sky-300">
                <Activity size={11} /> New from {firstOf(name)}
              </div>
              <ul className="mb-0 list-none pl-0">
                {items.map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i} className="flex gap-2 py-[3px] text-[12.5px] leading-snug text-slate-700 dark:text-slate-200">
                    <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-sky-500" />
                    <span className="min-w-0 flex-1 break-words">{activityLine(x.type, x.meta, labels)}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{ago(x.at, now)}</span>
                  </li>
                ))}
              </ul>
              {more > 0 ? <div className="mt-1 text-[11.5px] text-slate-400">and {more} more</div> : null}
              <div className="mt-2.5 text-[11px] text-slate-400">Open them to see it. That clears this.</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default ActivityMark;
